import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { UserStorage } from '@/services/user-storage';
import { authService, User } from '@/services/auth-service';
import { setAuthenticatedUserContext } from '@/services/app-insights-service';
import { isUserLoggedIn } from '@/util/helpers';

interface CurrentUserContextType {
  user: User | undefined;
  setUser: (user: User | undefined) => void;
}

const CurrentUserContext = createContext<CurrentUserContextType | null>(null);

interface CurrentUserProviderProps {
  children: ReactNode;
}

const userStorage = new UserStorage();

export function CurrentUserProvider({ children }: CurrentUserProviderProps) {
  const [user, setUser] = useState(userStorage.current);

  useEffect(() => authService.setOnChange(setUser), []);

  return (
    <CurrentUserContext.Provider value={{ user, setUser }}>{children}</CurrentUserContext.Provider>
  );
}

export function useCurrentUser() {
  const contextData = useContext(CurrentUserContext);

  if (!contextData) {
    throw new Error('useCurrentUser must be used within a CurrentUserProvider');
  }

  const setUserData = (token: string) => {
    userStorage.saveToken(token);
    const user = userStorage.current;
    if (user) {
      setAuthenticatedUserContext(user.id);
    }

    contextData.setUser(user);
  };

  const logout = () => {
    authService.logout();
  };

  return { user: contextData.user, setUserData, logout };
}

export function useLoggedUser() {
  const contextData = useCurrentUser();
  if (isUserLoggedIn(contextData.user)) {
    return { ...contextData, user: contextData.user };
  }

  throw new Error('useLoggedUser must be used within PrivateOutlet');
}
